import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Clock, X, Save, Plus } from 'lucide-react';
import { getMobileStyles } from '../styles/mobileDesignSystem';
import MobileCard from './MobileCard';

const MobileAddGigModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingGig = null 
}) => {
  const [formData, setFormData] = useState({
    title: editingGig?.title || '',
    date: editingGig?.date || '',
    time: editingGig?.time || '',
    venue: editingGig?.venue || '',
    description: editingGig?.description || ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.date) {
      return;
    }
    
    onSave({
      ...formData,
      id: editingGig?.id || Date.now().toString()
    });
    
    // Reset form
    setFormData({
      title: '',
      date: '',
      time: '',
      venue: '',
      description: ''
    });
    
    onClose();
  };

  const handleClose = () => {
    // Reset form on close
    setFormData({
      title: '',
      date: '',
      time: '',
      venue: '',
      description: ''
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[99999]"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md"
          >
            <MobileCard className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-blue-900/20 border-2 border-blue-500/60 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {editingGig ? 'Edit Gig' : 'Add New Gig'}
                    </h2>
                    <p className="text-gray-400 text-sm">
                      {editingGig ? 'Update your gig details' : 'Schedule a new performance'}
                    </p>
                  </div>
                </div>
                <motion.button
                  onClick={handleClose}
                  className="w-8 h-8 bg-gray-800/50 border border-gray-600 rounded-full flex items-center justify-center"
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-4 h-4 text-gray-400" />
                </motion.button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Gig Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Gig Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter gig title"
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Time
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Venue */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Venue
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      name="venue"
                      value={formData.venue}
                      onChange={handleInputChange}
                      placeholder="Enter venue name"
                      className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Add any additional details..."
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <motion.button
                    type="button"
                    onClick={handleClose}
                    className={`flex-1 ${getMobileStyles.button('outline', 'md')}`}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancel
                  </motion.button>
                  
                  <motion.button
                    type="submit"
                    className={`flex-1 ${getMobileStyles.button('primary', 'md')}`}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      {editingGig ? (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Update Gig</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          <span>Add Gig</span>
                        </>
                      )}
                    </div>
                  </motion.button>
                </div>
              </form>
            </MobileCard>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileAddGigModal;
